import { useServerFn } from "@tanstack/react-start";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ExternalLink, FileUp, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { listAllPosts, deletePost } from "@/lib/blog.functions";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: PostsList,
  head: () => ({ meta: [
    { title: "Posts | KCN Admin" },
    { name: "description", content: "Manage Kristan Cole Network blog posts and publication dates." },
    { property: "og:title", content: "Posts | KCN Admin" },
    { property: "og:description", content: "Manage Kristan Cole Network blog posts and publication dates." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex,nofollow" },
  ] }),
});

function PostsList() {
  const listFn = useServerFn(listAllPosts);
  const delFn = useServerFn(deletePost);
  const [posts, setPosts] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      setPosts(await listFn({}));
    } catch (e: any) {
      setErr(e.message);
    }
  }
  useEffect(() => { load(); }, []);

  async function onDelete(id: string, title: string) {
    if (!confirm(`Delete "${title}"?`)) return;
    await delFn({ data: { id } });
    load();
  }

  return (
    <div>
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-3 mb-6 sm:flex sm:justify-between">
        <h1 className="font-display text-3xl font-light leading-tight">Posts</h1>
        <Button asChild className="h-11 shrink-0 sm:order-2">
          <Link to="/admin/new"><Plus />New post</Link>
        </Button>
        <div className="col-span-2 flex min-w-0 items-center gap-2 sm:ml-auto">
          <Button asChild variant="outline" className="h-11">
            <Link to="/admin/import"><FileUp />Import CSV</Link>
          </Button>
          <Button asChild variant="ghost" className="h-11">
            <Link to="/blog"><ExternalLink />View blog</Link>
          </Button>
        </div>
      </div>

      {err ? <p className="text-primary mb-4">{err}</p> : null}
      {!posts ? (
        <p className="text-foreground/65">Loading…</p>
      ) : posts.length === 0 ? (
        <p className="text-foreground/65">No posts yet. Create one or import a CSV.</p>
      ) : (
        <div>
          <div className="hidden xl:grid grid-cols-[minmax(0,1fr)_8rem_11rem_7rem] items-center gap-6 border-b border-border bg-surface px-5 py-3 text-xs font-medium text-muted-foreground" aria-hidden="true">
            <span>Post</span><span>Status</span><span>Publish date</span><span className="text-right">Actions</span>
          </div>
          <ul className="divide-y divide-border border-y border-border xl:border-t-0" aria-label="Blog posts">
            {posts.map((p) => {
                const when = p.status === "scheduled" ? p.scheduled_for : p.published_at;
                return (
                  <li key={p.id} className="grid min-w-0 gap-4 py-5 sm:px-1 xl:grid-cols-[minmax(0,1fr)_8rem_11rem_7rem] xl:items-center xl:gap-6 xl:px-5">
                    <div className="min-w-0">
                      <Link to="/admin/edit/$id" params={{ id: p.id }} className="block text-base leading-6 font-medium hover:text-primary [overflow-wrap:anywhere]">{p.title}</Link>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground [overflow-wrap:anywhere]">/{p.slug}</p>
                    </div>
                    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-end gap-3 xl:contents">
                      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 xl:contents">
                        <span className={`w-fit shrink-0 rounded-sm px-2 py-1 text-xs font-medium capitalize ${
                          p.status === "published" ? "bg-success/10 text-success" :
                          p.status === "scheduled" ? "bg-primary/10 text-primary" :
                          "bg-muted text-muted-foreground"
                        }`}>{p.status}</span>
                        <span className="text-xs leading-5 text-muted-foreground xl:text-sm">
                          {when ? <time dateTime={when} title={new Date(when).toLocaleString()}>{new Date(when).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}<span className="block xl:mt-0.5">{new Date(when).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span></time> : "No publish date"}
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center justify-end gap-1">
                        <Button asChild variant="outline" className="h-11 px-3" aria-label={`Edit ${p.title}`}>
                          <Link to="/admin/edit/$id" params={{ id: p.id }}><Pencil /><span>Edit</span></Link>
                        </Button>
                        <Button variant="ghost" className="h-11 w-11 text-primary" onClick={() => onDelete(p.id, p.title)} aria-label={`Delete ${p.title}`} title="Delete post"><Trash2 /></Button>
                      </div>
                    </div>
                  </li>
                );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
